/*******************************************************************************
* Licensed Materials - Property of HCL
* (c) Copyright HCL Technologies Ltd. 2020, 2023. All Rights Reserved.

* Note to U.S. Government Users Restricted Rights:
* Use, duplication or disclosure restricted by GSA ADP Schedule
**************************************************************/

import React from "react";
import {Panel, Text, BackgroundPanel, SwitchButtons } from "vr-react-components";
import JobRoomWorkstationTooltip from "../../../widgets/JobRoomWorkstationTooltip";
import { useSelector, useDispatch } from "react-redux";
import BarPanelAux from "./BarPanelAux";
import { setChartTimeSelection, setRightPanelSelector } from "../../../store/slice/roomSlices/jobRoomSlice";

const RightPanelDesktop = React.memo(({ width, height }) => {
    const chartTimeSelection = useSelector((state) => state.jobroom.chartTimeSelection);
    const selectedChart = useSelector((state) => state.jobroom.rightPanelSelector);
    const selectedJob = useSelector((state) => state.jobroom.selectedJob);
    const canModify = useSelector((state) => state.private.device.canModify);

    const dispatch = useDispatch();

    const innerWidth = width * 0.9;
    const innerHeight = height * 0.9;
    const padding = innerWidth / 50;
    const textSize = 45 / innerWidth;

    return (
        <BackgroundPanel backgroundColor={"#121212"} width={width} height={height}>
            <JobRoomWorkstationTooltip
                backgroundColor={"#262626"}
                width={innerWidth - padding}
                height={innerHeight * 0.4 - padding}
                position={`0 ${innerHeight / 2 - (innerHeight * 0.4 - padding) / 2
                    } 0.001`}
                textSizeDecimal={textSize}
                element={{
                    name: selectedJob.workstation.name,
                    type: selectedJob.workstation.type,
                    limit: selectedJob.workstation.limit,
                    runningJobs: selectedJob.workstationJobsDetails
                        ? selectedJob.workstationJobsDetails.running
                        : 0,
                    status: selectedJob.workstation.status,
                    jobReady: selectedJob.workstationJobsDetails
                        ? selectedJob.workstationJobsDetails.ready
                        : 0,
                }}
            />
            <Panel
                width={innerWidth - padding}
                height={innerHeight * 0.6 - padding}
                backgroundColor="#262626"
                panel
                aframeClasses={canModify ? "collidable" : ""}
                position={`0 ${-innerHeight / 2 + (innerHeight * 0.6 - padding) / 2
                    } 0.001`}
                title="Job statistics (by month)"
                textSizeDecimal={textSize * 0.8}
                selection
                selectionChoices={["This month", "2 months ago", "3 months ago"]}
                currentSelection={chartTimeSelection}
                setSelectionChoices={(newChoices) => {
                    dispatch(setChartTimeSelection(newChoices))
                }}
            >
                <a-entity position={`0 ${innerHeight * 0.2} 0`}>
                    <SwitchButtons
                        height={innerHeight * 0.05}
                        width={innerWidth - padding}
                        aframeClasses={canModify ? "collidable" : ""}
                        options={[
                            {
                                element: "Starting time",
                                onClickHandler: () => {
                                    dispatch(setRightPanelSelector("Starting time"));
                                },
                            },
                            {
                                element: "Job Duration",
                                onClickHandler: () => {
                                    dispatch(setRightPanelSelector("Job Duration"));
                                },
                            },
                        ]}
                        textSizeDecimal={textSize * 1.2}
                        selected={selectedChart}
                    />
                </a-entity>
                {selectedChart === "Starting time" ? (
                    <>
                        <Text
                            text="nr. of runs by hour range"
                            width={innerWidth}
                            sizeDecimal={textSize * 1.5}
                            position={`${(-innerWidth / 2) * 0.9} ${innerHeight * 0.15
                                } 0.001`}
                        />
                        <BarPanelAux
                            flat
                            stateName="dataChartStartTime"
                            width={innerWidth}
                            height={innerHeight * 0.4}
                            position={`0 ${-innerHeight * 0.05} 0`}
                        />
                    </>
                ) : (
                    <>
                        <Text
                            text="nr. of runs by minute range"
                            width={innerWidth}
                            sizeDecimal={textSize * 1.5}
                            position={`${(-innerWidth / 2) * 0.9} ${innerHeight * 0.15
                                } 0.001`}
                        />
                        <BarPanelAux
                            flat
                            stateName="dataChartDuration"
                            width={innerWidth}
                            height={innerHeight * 0.4}
                            position={`0 ${-innerHeight * 0.05} 0`}
                        />
                    </>
                )}
            </Panel>
        </BackgroundPanel>
    );
});

export default RightPanelDesktop